import React, { useState, useMemo } from 'react';
import { Trophy, TrendingUp, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/**
 * Sales performance leaderboard table
 * Shows ARR and Services OTR by sales rep with sorting
 */
const SalesLeaderboard = ({ data, viewMode = 'pipeline' }) => {
  const [sortField, setSortField] = useState('revenue'); // Default sort by total revenue
  const [sortDirection, setSortDirection] = useState('desc');

  const isPipeline = viewMode === 'pipeline';
  const title = isPipeline ? 'Sales Pipeline Leaderboard' : 'Sales Won Leaderboard';

  // Sort data
  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'quotes':
          aVal = a.quotes || 0;
          bVal = b.quotes || 0;
          break;
        case 'accepted':
          aVal = a.accepted || 0;
          bVal = b.accepted || 0;
          break;
        case 'rate':
          aVal = a.rate || 0;
          bVal = b.rate || 0;
          break;
        case 'arr':
          aVal = a.arr || 0;
          bVal = b.arr || 0;
          break;
        case 'servicesOTR':
          aVal = a.servicesOTR || 0;
          bVal = b.servicesOTR || 0;
          break;
        case 'revenue':
        default:
          aVal = a.revenue || 0;
          bVal = b.revenue || 0;
          break;
      }
      if (sortField === 'name') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronsUpDown size={14} className="text-slate-300" />;
    return sortDirection === 'asc'
      ? <ChevronUp size={14} className="text-blue-600" />
      : <ChevronDown size={14} className="text-blue-600" />;
  };

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Trophy size={20} className="text-yellow-500" />
          {title}
        </h3>
        <div className="text-center py-8 text-slate-500">
          No {isPipeline ? 'pipeline' : 'won'} data available for the selected period
        </div>
      </div>
    );
  }

  const SortableHeader = ({ field, children, align = 'left' }) => (
    <th
      onClick={() => handleSort(field)}
      className={`py-3 px-2 text-sm font-semibold text-slate-600 cursor-pointer hover:bg-slate-50 transition-colors ${
        align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <div className={`flex items-center gap-1 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : ''}`}>
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  const formatCurrency = (value) => {
    if (!value) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const getRankBadge = (index) => {
    if (index === 0) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (index === 1) return 'bg-slate-100 text-slate-600 border-slate-300';
    if (index === 2) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-slate-50 text-slate-500 border-slate-200';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Trophy size={20} className="text-yellow-500" />
        {title}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-3 px-2 text-sm font-semibold text-slate-600">Rank</th>
              <SortableHeader field="name">Sales Rep</SortableHeader>
              <SortableHeader field="quotes" align="center">Quotes</SortableHeader>
              <SortableHeader field="accepted" align="center">Won</SortableHeader>
              <SortableHeader field="rate" align="center">Win Rate</SortableHeader>
              <SortableHeader field="arr" align="right">ARR</SortableHeader>
              <SortableHeader field="servicesOTR" align="right">Services OTR</SortableHeader>
              <SortableHeader field="revenue" align="right">Total</SortableHeader>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((rep, index) => (
              <tr key={rep.email} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-2">
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold border-2 ${getRankBadge(index)}`}>
                    {index + 1}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <div>
                    <div className="font-medium text-slate-800">{rep.name || 'Unknown'}</div>
                    <div className="text-xs text-slate-500">{rep.email}</div>
                  </div>
                </td>
                <td className="py-3 px-2 text-center">
                  <span className="text-slate-700 font-medium">{rep.quotes}</span>
                </td>
                <td className="py-3 px-2 text-center">
                  <span className="text-green-600 font-medium">{rep.accepted}</span>
                </td>
                <td className="py-3 px-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <TrendingUp size={14} className={rep.rate >= 50 ? 'text-green-500' : 'text-slate-400'} />
                    <span className={`font-medium ${rep.rate >= 50 ? 'text-green-600' : 'text-slate-600'}`}>
                      {rep.rate}%
                    </span>
                  </div>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-medium text-blue-600">{formatCurrency(rep.arr)}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-medium text-purple-600">{formatCurrency(rep.servicesOTR)}</span>
                </td>
                <td className="py-3 px-2 text-right">
                  <span className="font-semibold text-slate-800">{formatCurrency(rep.revenue)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesLeaderboard;
